import { merge } from "lodash";
import { THEMES } from "../constants";
import { lightShadows, darkShadows } from "./shadows";
import {
  alpha,
  Direction,
  Theme,
  ThemeOptions,
  createTheme,
  responsiveFontSizes,
} from "@mui/material";

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

const baseOptions: ThemeOptions = {
  direction: "ltr",
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: "75%",
          width: "75%",
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: "small",
        autoComplete: "off",
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
        ChipProps: {
          size: "small",
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
        },
        html: {
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          height: "100%",
          width: "100%",
        },
        body: {
          height: "100%",
        },
        "#root": {
          height: "100%",
        },
        "#nprogress .bar": {
          zIndex: "2000 !important",
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: "hidden",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "auto",
          marginRight: "16px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-disabled": {
            color:
              theme.palette.mode === "dark"
                ? theme.palette.text.primary
                : alpha(theme.palette.text.primary, 0.87),
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-disabled": {
            color:
              theme.palette.mode === "dark"
                ? theme.palette.text.secondary
                : alpha(theme.palette.text.secondary, 0.85),
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-disabled": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor:
                theme.palette.mode === "dark"
                  ? theme.palette.divider
                  : alpha(theme.palette.text.primary, 0.38),
              borderWidth: "1px",
              borderStyle: "dashed",
            },
            "& .MuiSvgIcon-root": {
              display: "none",
            },
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          "&.Mui-disabled": {
            opacity: 1,
            color:
              theme.palette.mode === "dark"
                ? theme.palette.text.primary
                : alpha(theme.palette.text.primary, 0.87),
            WebkitTextFillColor:
              theme.palette.mode === "dark"
                ? theme.palette.text.primary
                : alpha(theme.palette.text.primary, 0.87),
          },
        }),
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600,
    },
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: "3.5rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "3rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "2.25rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "2rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
    overline: {
      fontWeight: 600,
    },
  },
};

const themesOptions: Record<string, ThemeOptions> = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }) => ({
            "&.Mui-disabled": {
              opacity: 1,
              color: alpha(theme.palette.text.primary, 0.87),
              WebkitTextFillColor: alpha(theme.palette.text.primary, 0.87),
            },
            "&::placeholder": {
              opacity: 0.3,
              color: "#42526e",
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-disabled": {
              opacity: 1,
              color: alpha(theme.palette.text.primary, 0.87),
              WebkitTextFillColor: alpha(theme.palette.text.primary, 0.87),
              backgroundColor: "hsl(0, 0%, 95%)",
            },
          }),
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.6)",
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.6)",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-disabled": {
              color: alpha(theme.palette.text.primary, 0.87),
            },
          }),
          asterisk: {
            color: "#f44336",
            "&$error": {
              color: "#f44336",
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            "&:visited": {
              color: "#9c27b0",
            },
          },
        },
      },
    },
    palette: {
      action: {
        active: "#6b778c",
      },
      background: {
        default: "#f4f5f7",
        paper: "#ffffff",
      },
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
      },
      mode: "light",
      primary: {
        contrastText: "#ffffff",
        // main: "#5664d2",
        main: "#3e2465",
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
      },
      text: {
        primary: "#172b4d",
        secondary: "#6b778c",
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
      },
    },
    shadows: lightShadows,
  },
  [THEMES.DARK]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }) => ({
            "&.Mui-disabled": {
              opacity: 1,
              WebkitTextFillColor: theme.palette.text.primary,
              color: theme.palette.text.primary,
            },
            "&::placeholder": {
              opacity: 0.5,
              color: theme.palette.text.secondary,
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            backgroundColor: "#0F1C2E",
            "&.Mui-disabled": {
              opacity: 1,
              color: theme.palette.text.primary,
              WebkitTextFillColor: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.common.white, 0.08),
            },
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-disabled": {
              color: theme.palette.text.primary,
            },
          }),
          asterisk: {
            color: "#f44336",
            "&$error": {
              color: "#f44336",
            },
          },
        },
      },

      MuiLink: {
        styleOverrides: {
          root: {
            ":visited": {
              color: "#ce93d8",
            },
          },
        },
      },
    },

    palette: {
      mode: "dark",
      background: {
        default: "#0F1C2E",
        paper: "#1f2b3e",
      },
      divider: "#374357",
      error: {
        contrastText: "#e0e0e0",
        main: "#f44336",
      },
      primary: {
        contrastText: "#374357",
        main: "#acc2ef",
      },
      success: {
        contrastText: "#e0e0e0",
        main: "#388e3c",
      },
      info: {
        contrastText: "#e0e0e0",
        main: "#0288d1",
      },
      text: {
        primary: "#acc2ef",
        secondary: "#e0e0e0",
      },
      warning: {
        contrastText: "#e0e0e0",
        main: "#f57c00",
      },
    },
    shadows: darkShadows,
  },
  [THEMES.NATURE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }) => ({
            "&.Mui-disabled": {
              opacity: 1,
              WebkitTextFillColor: theme.palette.text.primary,
              color: theme.palette.text.primary,
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-disabled": {
              opacity: 1,
              color: theme.palette.text.primary,
              WebkitTextFillColor: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.common.white, 0.08),
            },
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          asterisk: {
            color: "#f44336",
            "&$error": {
              color: "#f44336",
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            ":visited": {
              color: "#ce93d8",
            },
          },
        },
      },
    },
    palette: {
      background: {
        default: "#1c2531",
        paper: "#293142",
      },
      divider: "rgba(145, 158, 171, 0.24)",
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
      },
      mode: "dark",
      primary: {
        contrastText: "#ffffff",
        main: "#01ab56",
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
      },
      text: {
        primary: "#ffffff",
        secondary: "#919eab",
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
      },
    },
    shadows: darkShadows,
  },
};

export const createCustomTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      config.roundedCorners && {
        shape: {
          borderRadius: 8,
        },
      },
      { direction: config.direction },
    ),
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
